import { useMemo, useState } from 'react'
import TimeSlotPicker from './TimeSlotPicker'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export default function ScheduleCalendarSection({ astrologerId, selectedDate, selectedSlot, onSelectDate, onSelectSlot }) {
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])
  const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : today.getMonth())
  const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : today.getFullYear())

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay()
  const isCurrentMonth = viewMonth === today.getMonth() && viewYear === today.getFullYear()

  const goPrevMonth = () => {
    if (isCurrentMonth) return
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1) } else { setViewMonth((m) => m - 1) }
  }
  const goNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1) } else { setViewMonth((m) => m + 1) }
  }

  const handlePickDay = (day) => {
    onSelectDate(new Date(viewYear, viewMonth, day))
  }

  return (
    <div className="space-y-8">
      <div className="info-card overflow-hidden">
        <div className="h-[3px] w-full bg-gradient-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
        <div className="p-6 border-b border-[#D7C29A]/20 bg-gradient-to-r from-[#FFFCF5] to-[#FEF6E4] flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] font-mono font-normal text-secondary uppercase tracking-[0.3em] mb-1">Celestial View</span>
            <h4 className="text-xl font-cinzel text-[#1C140A]">{MONTH_NAMES[viewMonth]} {viewYear}</h4>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={goPrevMonth}
              disabled={isCurrentMonth}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#D7C29A]/30 text-[#1C140A]/50 hover:bg-secondary/10 hover:text-[#1C140A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              aria-label="Previous month"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goNextMonth}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#D7C29A]/30 text-[#1C140A]/50 hover:bg-secondary/10 hover:text-[#1C140A] transition-colors"
              aria-label="Next month"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-[9px] font-bold uppercase tracking-widest text-[#8B7355] py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstWeekday }, (_, i) => <div key={`pad-${i}`} aria-hidden="true" />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const cellDate = new Date(viewYear, viewMonth, day)
              const isPast = cellDate < today
              const isToday = isSameDay(cellDate, today)
              const isActive = selectedDate ? isSameDay(cellDate, selectedDate) : false

              let cls = 'aspect-square rounded-lg flex items-center justify-center text-xs font-semibold transition-all '
              if (isActive) cls += 'text-white shadow-[0_8px_18px_-6px_rgba(28,20,10,.55),0_0_0_3px_rgba(242,160,7,.22)]'
              else if (isPast) cls += 'text-[#1C140A]/20 cursor-not-allowed'
              else if (isToday) cls += 'border border-secondary text-[#1C140A] hover:bg-secondary hover:text-white'
              else cls += 'text-[#1C140A]/75 hover:bg-secondary/15 hover:text-[#1C140A]'

              return (
                <button
                  key={day}
                  type="button"
                  disabled={isPast}
                  onClick={() => handlePickDay(day)}
                  className={cls}
                  style={isActive ? { background: 'linear-gradient(135deg, #1C140A 0%, #2E1206 100%)' } : undefined}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <TimeSlotPicker
        astrologerId={astrologerId}
        selectedDate={selectedDate}
        selectedSlot={selectedSlot}
        onSelectSlot={onSelectSlot}
      />
    </div>
  )
}
